import type { CommandResult } from '@travel-planner/shared/schemas';
import type { CommandSession } from '../model/use-agent-panel-store';
import { useEffect, useRef } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { Button, Spinner, Text } from '@/shared/ui';
import { getInputWidget } from '../model/input-widget-registry';
import { getResultRenderer } from '../model/result-renderer-registry';
import { useAgentPanelStore } from '../model/use-agent-panel-store';
import css from './command-session-panel.module.scss';
import { WorkflowSummaryCard } from './workflow-summary-card';

const styles = pickCss(css, css.root);
const bubbleStyles = pickCss(css, css.bubble);

export type CommandSessionPanelProps = {
  // Click handler used to send a candidate-pick phrase as a follow-up turn
  // (e.g. "productId: <id>"). Free-text follow-up answers come from the
  // top search input, not from this component anymore.
  onSendTurn: (utterance: string) => void | Promise<void>;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  // When the active commandSession finishes inside a workflow, the panel
  // shows the result + a Continue button that fires this callback. Without
  // it, a finished step looks like the workflow ate the result silently.
  // Optional: when omitted, no Continue button is shown (single-command
  // sessions don't need it).
  onContinueWorkflow?: (result: CommandResult) => void | Promise<void>;
  // Optional: shown next to Continue on `loopable` steps so the consultant
  // can re-run the step with fresh seed (e.g. pick another category).
  // Selections from the current pass stay intact in workflowSession.visits.
  onRestartWorkflowStep?: () => void | Promise<void>;
  isPending?: boolean;
};

// True when the active workflow step is the last one in the catalog. Used
// to relabel the post-step button "Continue" → "Finish" so the consultant
// understands clicking it ends the workflow rather than advancing to a
// next step that doesn't exist.
const isLastWorkflowStep = (workflow: {
  activeStepIndex: number;
  steps: ReadonlyArray<unknown>;
}): boolean => workflow.activeStepIndex >= workflow.steps.length - 1;

const formatCollectedValue = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return JSON.stringify(value);
};

const ResultPanel = ({ result }: { result: CommandResult }) => {
  const Renderer = getResultRenderer(result.kind);
  if (Renderer) return <Renderer result={result} />;
  return (
    <div className={css.done}>
      <Text as="p" size="sm" weight="medium">
        Done
      </Text>
      <Text as="p" size="sm">
        {result.humanSummary}
      </Text>
    </div>
  );
};

// Exported so the panel shell can render the conversation log at the top of
// the body (above chips, summary, and result renderers). Auto-scrolls to
// the latest turn whenever the history grows.
export const HistoryView = ({
  history,
}: {
  history: CommandSession['history'];
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [history.length]);
  if (history.length === 0) return null;
  return (
    <div className={css.history} ref={ref}>
      {history.map((turn, idx) => (
        <div
          className={bubbleStyles({ role: turn.role })}
          key={`${turn.role}-${idx}`}
        >
          <Text as="span" size="base">
            {turn.content}
          </Text>
        </div>
      ))}
    </div>
  );
};

const CollectedSummary = ({
  collected,
}: {
  collected: Record<string, unknown>;
}) => {
  // Hide reserved/internal keys (underscore-prefixed) and bulky shapes like
  // `_candidates` from the chip summary — they're for internal flow control.
  const entries = Object.entries(collected).filter(
    ([k, v]) =>
      !k.startsWith('_') &&
      v !== undefined &&
      v !== null &&
      v !== '' &&
      typeof v !== 'object'
  );
  if (entries.length === 0) return null;
  return (
    <div className={css.collected}>
      {entries.map(([key, value]) => (
        <div className={css.collectedRow} key={key}>
          <Text className={css.collectedKey} size="sm" weight="medium">
            {key}:
          </Text>
          <Text size="sm">{formatCollectedValue(value)}</Text>
        </div>
      ))}
    </div>
  );
};

// A candidate is a clickable chip injected by a command's `beforeAsk` or by
// a server-side validator. Click → the picker constructs a phrase the
// planner can extract on the next turn ("I'll pick X. The {fieldName} is
// {id}."). `fieldName` defaults to 'productId' for backwards compatibility
// with the original update_product candidate flow.
type Candidate = {
  id: string;
  title: string;
  // Optional secondary text under the title.
  subtitle?: string | null;
  // Legacy fields used by update_product candidates — kept for backwards
  // compatibility. New candidate sets should set `subtitle` directly.
  city?: string | null;
  country?: string | null;
  imageUrl?: string | null;
  // Which collected field the picker should set on the planner round-trip.
  // Defaults to 'productId' so the original update_product flow keeps working.
  fieldName?: string;
};

const isCandidateArray = (value: unknown): value is Candidate[] =>
  Array.isArray(value) &&
  value.every(
    (c) =>
      !!c &&
      typeof c === 'object' &&
      typeof (c as { id: unknown }).id === 'string' &&
      typeof (c as { title: unknown }).title === 'string'
  );

const candidateSubtitle = (c: Candidate): string | null => {
  if (c.subtitle != null && c.subtitle.length > 0) return c.subtitle;
  const cityCountry = [c.city, c.country].filter(Boolean).join(', ');
  return cityCountry.length > 0 ? cityCountry : null;
};

const CandidatePicker = ({
  candidates,
  onPick,
  disabled,
  label,
}: {
  candidates: Candidate[];
  onPick: (id: string) => void;
  disabled?: boolean;
  label?: string;
}) => (
  <div className={css.candidates}>
    <Text as="p" size="sm" className={css.candidatesLabel}>
      {label ?? 'Pick one:'}
    </Text>
    {candidates.map((c) => {
      const sub = candidateSubtitle(c);
      const showId = (c.fieldName ?? 'productId') === 'productId';
      return (
        <button
          className={css.candidate}
          disabled={disabled}
          key={c.id}
          onClick={() => onPick(c.id)}
          type="button"
        >
          {c.imageUrl ? (
            <img className={css.candidateThumb} src={c.imageUrl} alt="" />
          ) : (
            <div aria-hidden="true" className={css.candidateThumbPlaceholder} />
          )}
          <span className={css.candidateText}>
            <Text className={css.candidateTitle} size="sm" weight="medium">
              {c.title}
            </Text>
            {sub && (
              <Text size="xs" variant="muted">
                {sub}
              </Text>
            )}
            {showId && (
              <span
                className={css.candidateId}
                data-testid="candidate-id"
                title="Product UUID (debug)"
              >
                {c.id}
              </span>
            )}
          </span>
        </button>
      );
    })}
  </div>
);

export const CommandSessionPanel = ({
  onSendTurn,
  onConfirm,
  onCancel,
  onContinueWorkflow,
  onRestartWorkflowStep,
  isPending = false,
}: CommandSessionPanelProps) => {
  const session = useAgentPanelStore((s) => s.commandSession);
  const workflow = useAgentPanelStore((s) => s.workflowSession);

  if (!session) return null;

  // History is now rendered by the parent (`AgentPanel`) at the top of the
  // body, so the conversation log sits above chips/summary/result. We
  // intentionally do NOT render `HistoryView` here.
  return (
    <div className={styles()}>
      {session.error && (
        <div className={css.error} role="alert">
          <Text as="p" size="sm">
            {session.error}
          </Text>
        </div>
      )}

      {session.phase === 'collecting' && (
        <>
          <CollectedSummary collected={session.collected} />
          {(() => {
            const cands = session.collected._candidates;
            if (!isCandidateArray(cands)) return null;
            // Each candidate carries the destination field name (defaults to
            // 'productId' for backwards compat with the original update_product
            // flow). If that field is already filled in `collected`, the
            // consultant has already picked — hide the chips so we don't
            // re-render them after a successful pick.
            const fieldName = cands[0]?.fieldName ?? 'productId';
            if (session.collected[fieldName] !== undefined) return null;
            const labelByField: Record<string, string> = {
              productId: 'Pick a product:',
              category: 'Pick a category:',
            };
            return (
              <CandidatePicker
                candidates={cands}
                disabled={isPending}
                label={labelByField[fieldName]}
                onPick={(id) => {
                  // Send a structured one-line phrase the planner can map
                  // directly to a tool call. Lowercase only, no flowery
                  // wording — the LLM sometimes echoes the chip *title* into
                  // the field value (e.g. "Restaurant" instead of
                  // "restaurant"), which fails the enum schema. Sending only
                  // the lowercase id avoids the ambiguity entirely.
                  void onSendTurn(`${fieldName}: ${id}`);
                }}
              />
            );
          })()}
          {(() => {
            // Inline input widget — when a param declares
            // `inputWidget: '<kind>'`, render the registered component here so
            // the consultant gets a structured picker (e.g. CRM contact
            // search) instead of typing into the top search bar. Free-text
            // replies for plain string params still come from the top input.
            const widgetKind = session.pendingInputWidget;
            if (!widgetKind) return null;
            const Widget = getInputWidget(widgetKind);
            if (!Widget) return null;
            return (
              <Widget
                disabled={isPending}
                onSubmit={(value) => void onSendTurn(value)}
              />
            );
          })()}
        </>
      )}

      {session.phase === 'confirm' && (
        <div className={css.confirm}>
          <Text as="p" size="sm" weight="medium">
            {session.confirmSummary}
          </Text>
          <CollectedSummary collected={session.collected} />
          <div className={css.confirmActions}>
            <Button onClick={onCancel} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={isPending}
              onClick={() => void onConfirm()}
              type="button"
              variant="primary"
            >
              Confirm
            </Button>
          </div>
        </div>
      )}

      {session.phase === 'executing' && (
        <div className={css.executing} role="status">
          <Spinner size="sm" />
          <Text as="span" size="sm">
            Working...
          </Text>
        </div>
      )}

      {/* Workflow finished — show the trip summary card and skip the
          per-step result panel. The summary reads everything it needs from
          workflowSession (completedResults + visits). */}
      {workflow && workflow.phase === 'workflow-done' && (
        <WorkflowSummaryCard onClose={onCancel} />
      )}

      {session.phase === 'done' &&
        session.result &&
        (!workflow || workflow.phase !== 'workflow-done') && (
          <>
            <ResultPanel result={session.result} />
            {/* When this finished step belongs to an active workflow, the
              consultant has to acknowledge the result before moving on.
              Without an explicit Continue, the auto-advance was making the
              result panel disappear before the consultant could read it. */}
            {workflow &&
              workflow.phase === 'step-active' &&
              onContinueWorkflow &&
              session.result && (
                <div className={css.confirmActions}>
                  <Button onClick={onCancel} type="button" variant="outline">
                    Cancel
                  </Button>
                  {/* "Pick another category" — only on loopable steps with
                    a restart handler wired up. Selections from this pass
                    stay; clicking re-opens the step's chip picker. */}
                  {workflow.steps[workflow.activeStepIndex]?.loopable &&
                    onRestartWorkflowStep && (
                      <Button
                        disabled={isPending}
                        onClick={() => void onRestartWorkflowStep()}
                        type="button"
                        variant="outline"
                      >
                        Pick another category
                      </Button>
                    )}
                  <Button
                    disabled={isPending}
                    onClick={() => {
                      if (session.result)
                        void onContinueWorkflow(session.result);
                    }}
                    type="button"
                    variant="primary"
                  >
                    {isLastWorkflowStep(workflow) ? 'Finish' : 'Continue'}
                  </Button>
                </div>
              )}
          </>
        )}

      {session.phase === 'cancelled' && (
        <div className={css.cancelled}>
          <Text as="p" size="sm">
            Command cancelled.
          </Text>
        </div>
      )}
    </div>
  );
};
